import type {
  CoviChatItem,
  CoviToolStep,
  CoviTurnContext,
} from "../model/types"
import { buildActionTitle, type DroppedProposal } from "./actions"
import { boundHistory } from "./history"
import { toolLabel } from "./toolSteps"
import { snapshotCoviViewContext } from "./viewContext"

const sessionSeed = Math.random().toString(36).slice(2, 8)
let itemCounter = 0

/** Cap on one serialized outcome clause — this rides every subsequent prompt. */
const MAX_OUTCOME_CHARS = 160
const MAX_REPORTED_DROPPED_PROPOSALS = 4
const MAX_DROPPED_KIND_CHARS = 64
const MAX_DROPPED_REASON_CHARS = 120

/** Charter-coupled wording — read actionOutcome before touching it. */
const STAGED_OUTCOME = "staged in the editor, not yet saved"
/** Tail of the test-set editor bridge's receipt, e.g. "2 test cases staged for review". */
const STAGED_DETAIL_SUFFIX = /\s*staged(?:\s+for\s+review)?$/i
/** Resource ids carry a capital, digit, or underscore; bare route roots do not. */
const RESOURCE_ID_SHAPE = /[A-Z0-9_]/

interface ChatHistoryMessage {
  role: string
  content: string
}

export function nextChatItemId(prefix: string): string {
  itemCounter += 1
  return `${prefix}_${sessionSeed}_${itemCounter}`
}

function clampOutcome(text: string, limit: number): string {
  const collapsed = text.trim().replace(/\s+/g, " ")
  return collapsed.length > limit ? `${collapsed.slice(0, limit)}…` : collapsed
}

/** Created-resource id from a receipt deep link, when the link points at one. */
function resourceIdFromHref(href: string | undefined): string | null {
  if (!href) {
    return null
  }
  const lastSegment = href.split(/[?#]/)[0].split("/").filter(Boolean).at(-1)
  return lastSegment && RESOURCE_ID_SHAPE.test(lastSegment) ? lastSegment : null
}

/**
 * Outcome clause for a settled action. Staged editor changes read differently
 * from executed ones on purpose: nothing is written until the user saves, so
 * reporting them as "done" is what makes Sofia re-propose work it already did.
 */
function settledOutcome(
  item: Extract<CoviChatItem, { kind: "action" }>,
): string {
  const detail = clampOutcome(item.detail ?? "", MAX_OUTCOME_CHARS)
  if (STAGED_DETAIL_SUFFIX.test(detail)) {
    const staged = detail.replace(STAGED_DETAIL_SUFFIX, "").trim()
    return staged.length > 0 ? `${STAGED_OUTCOME}: ${staged}` : STAGED_OUTCOME
  }
  const resourceId = resourceIdFromHref(item.href)
  const withId =
    resourceId && !detail.includes(resourceId)
      ? [detail, `(${resourceId})`].filter(Boolean).join(" ")
      : detail
  return withId.length > 0 ? `done: ${withId}` : "done"
}

/**
 * The staged / `done:` / `failed:` clauses are substring-matched by the agent
 * charter to tell a landed write from an unsaved one. Rewording one silently
 * stops that rule firing, and nothing on either side fails.
 */
function actionOutcome(
  item: Extract<CoviChatItem, { kind: "action" }>,
): string {
  switch (item.status) {
    case "proposed":
      return "awaiting confirmation"
    case "executing":
      return "in progress"
    case "dismissed":
      return "dismissed by the user"
    case "error": {
      const reason = clampOutcome(item.error ?? "", MAX_OUTCOME_CHARS)
      // An expired confirmation is not retryable — only a fresh proposal is.
      const expiry =
        item.expired === true ? " (confirmation expired, re-propose)" : ""
      return reason.length > 0
        ? `failed: ${reason}${expiry}`
        : `failed${expiry}`
    }
    case "done":
      return settledOutcome(item)
  }
}

function buildActionHistoryEntry(
  item: Extract<CoviChatItem, { kind: "action" }>,
): string {
  const rationale = item.proposal.rationale.trim()
  const summary = `Proposed action: ${buildActionTitle(item.proposal)} — ${actionOutcome(item)}`
  return rationale.length > 0 ? `${summary}\nReason: ${rationale}` : summary
}

function buildVisualizationHistoryEntry(
  item: Extract<CoviChatItem, { kind: "action" }>,
  includeSpec: boolean,
): string {
  const rawTitle = item.proposal.args.title
  const title =
    typeof rawTitle === "string" && rawTitle.trim().length > 0
      ? rawTitle.trim()
      : "chart"
  const summary = `Rendered visualization: ${title}`
  if (!includeSpec) {
    return summary
  }
  let spec = ""
  try {
    spec = JSON.stringify(item.proposal.args.spec) ?? ""
  } catch {
    spec = ""
  }
  return spec.length > 0
    ? `${summary}\nSpec:\n\`\`\`json\n${spec}\n\`\`\``
    : summary
}

export function buildToolItems(steps: CoviToolStep[]): CoviChatItem[] {
  return steps.map(
    (step): CoviChatItem => ({
      kind: "tool",
      id: nextChatItemId("tool"),
      name: step.name,
      summary: toolLabel(step.name),
      status: step.status,
      running: false,
      isError: step.is_error,
      isResearch: step.is_research,
    }),
  )
}

export function conversationIdForItems(items: CoviChatItem[]): string | null {
  const firstItemId = items[0]?.id
  return firstItemId ? `conv-${firstItemId}`.slice(0, 128) : null
}

/**
 * Proposals the proxy refused, awaiting their one report to Sofia. Keyed by
 * conversation: several threads can be resident at once, and a refusal one
 * thread reported must not ride out on another thread's turn. Client-only, so
 * it never spans users.
 */
const pendingDroppedProposals = new Map<string, DroppedProposal[]>()
/** Ceiling on queued conversations — a thread that never sends again must not pin entries forever. */
const MAX_PENDING_CONVERSATIONS = 64

/**
 * Stash the refusals a completion reported, bounded and de-duplicated per
 * conversation. Read off the wire, so every field is re-checked rather than
 * trusted.
 */
export function recordDroppedProposals(
  conversationId: string,
  value: unknown,
): void {
  if (!Array.isArray(value)) {
    return
  }
  let queue = pendingDroppedProposals.get(conversationId)
  if (!queue) {
    if (pendingDroppedProposals.size >= MAX_PENDING_CONVERSATIONS) {
      const oldest = pendingDroppedProposals.keys().next().value
      if (oldest !== undefined) {
        pendingDroppedProposals.delete(oldest)
      }
    }
    queue = []
    pendingDroppedProposals.set(conversationId, queue)
  }
  for (const entry of value) {
    if (queue.length >= MAX_REPORTED_DROPPED_PROPOSALS) {
      return
    }
    if (typeof entry !== "object" || entry === null) {
      continue
    }
    const { kind, reason } = entry as { kind?: unknown; reason?: unknown }
    if (typeof kind !== "string" || typeof reason !== "string") {
      continue
    }
    const drop: DroppedProposal = {
      kind: clampOutcome(kind, MAX_DROPPED_KIND_CHARS),
      reason: clampOutcome(reason, MAX_DROPPED_REASON_CHARS),
    }
    if (drop.kind.length === 0 || drop.reason.length === 0) {
      continue
    }
    const duplicate = queue.some(
      (pending) => pending.kind === drop.kind && pending.reason === drop.reason,
    )
    if (!duplicate) {
      queue.push(drop)
    }
  }
}

/** Turn context posted to the proxy; free-form beyond the fields set here. */
interface CoviTurnContextPayload extends Record<string, unknown> {
  dropped_proposals?: DroppedProposal[]
}

/**
 * Discard the refusals a landed send carried, identified by the context it
 * actually posted. A send that fails skips this so the next one retries the
 * report instead of dropping it. Clearing by carried entry rather than by
 * conversation is what keeps an overlapping send — nothing serializes them —
 * from deleting a refusal recorded after its own payload was built. Called
 * with no argument, everything goes (test reset).
 */
export function clearRecordedDroppedProposals(
  conversationId?: string,
  sentContext?: CoviTurnContextPayload,
): void {
  if (conversationId === undefined) {
    pendingDroppedProposals.clear()
    return
  }
  const queue = pendingDroppedProposals.get(conversationId)
  if (!queue) {
    return
  }
  const delivered = new Set(sentContext?.dropped_proposals)
  const remaining = queue.filter((entry) => !delivered.has(entry))
  if (remaining.length === 0) {
    pendingDroppedProposals.delete(conversationId)
    return
  }
  // Overwriting the value keeps the key's insertion position, so the
  // oldest-conversation eviction order survives a partial clear.
  pendingDroppedProposals.set(conversationId, remaining)
}

export function buildTurnContextPayload(
  context: CoviTurnContext,
  conversationId: string,
): CoviTurnContextPayload {
  const payload: CoviTurnContextPayload = {
    conversation_id: conversationId,
    // Advertise from the loaded browser bundle, including confirmation turns.
    alert_actions_version: 1,
  }
  // Attached here but kept until the caller confirms the turn landed
  // (clearRecordedDroppedProposals): a cancelled, refused, or failed send
  // retries the report instead of silently losing it. Still reported exactly
  // once — the clear after a landed turn is what stops a single bad turn from
  // riding along in every prompt that follows it. The entries handed out are
  // the queued objects themselves, so the clear can match on identity.
  const pending = pendingDroppedProposals.get(conversationId)
  if (pending && pending.length > 0) {
    payload.dropped_proposals = [...pending]
  }
  if (context.page) {
    payload.page = context.page
  }
  if (context.onboardingCounts) {
    payload.onboardingCounts = context.onboardingCounts
  }
  const { view, focus_stack: focusStack } = snapshotCoviViewContext()
  if (view) {
    payload.view = view
  }
  if (focusStack.length > 0) {
    payload.focus_stack = focusStack
  }
  return payload
}

export function buildChatHistory(items: CoviChatItem[]): ChatHistoryMessage[] {
  let latestVisualizationId: string | null = null
  for (const item of items) {
    if (
      item.kind === "action" &&
      item.proposal.kind === "render_visualization"
    ) {
      latestVisualizationId = item.id
    }
  }

  const history = items.reduce<ChatHistoryMessage[]>((messages, item) => {
    if (item.provisional === true) {
      return messages
    }
    if (
      (item.kind === "user" || item.kind === "assistant") &&
      item.text.trim().length > 0
    ) {
      messages.push({
        role: item.kind === "user" ? "user" : "assistant",
        content: item.text,
      })
      return messages
    }
    if (item.kind !== "action") {
      return messages
    }

    const serialized =
      item.proposal.kind === "render_visualization"
        ? buildVisualizationHistoryEntry(
            item,
            item.id === latestVisualizationId,
          )
        : buildActionHistoryEntry(item)
    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.role === "assistant") {
      lastMessage.content = `${lastMessage.content}\n\n${serialized}`
    } else {
      messages.push({ role: "assistant", content: serialized })
    }
    return messages
  }, [])
  return boundHistory(history)
}
